import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileSpreadsheet, History, ChevronRight, Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { fmtNumber, usePalette } from '../charts/chartKit';
import { isManualLeg, modeName, shortPlace, vehicleName, ManualDistanceIcon } from '../shipments/legLabels';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001'}/api`;

const CATEGORY = {
  upstream: { cat: 4, label: 'Cat 4 · Upstream' },
  downstream: { cat: 9, label: 'Cat 9 · Downstream' },
  6: { cat: 6, label: 'Cat 6 · Business travel' },
  7: { cat: 7, label: 'Cat 7 · Commuting' },
};
const TYPE_LABEL = { freight: 'Freight', business_travel: 'Business travel', employee_commute: 'Employee commute' };

const fmtDate = (iso, withTime = false) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, withTime
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' });
};

function CatPill({ value, palette }) {
  if (value === 'mixed') {
    return <span className="whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">Cat 4 + 9</span>;
  }
  const c = CATEGORY[value];
  if (!c) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
      <span className="h-2 w-2 rounded-[2px]" style={{ background: palette.category(c.cat) }} aria-hidden="true" />
      {c.label}
    </span>
  );
}

/**
 * Past bulk uploads. Each saved file is listed; opening one shows only the entries that file
 * created, taken from the same saved shipments / activities the dashboard shows (so entries deleted
 * since then are simply gone, and nothing is duplicated).
 *
 * props: shipments — the user's saved shipments (already loaded by the page)
 *        refreshKey — change it after a save to reload the list
 */
export default function UploadHistory({ shipments = [], refreshKey = 0 }) {
  const palette = usePalette();
  const [state, setState] = useState({ status: 'loading', items: [] });
  const [open, setOpen] = useState(null);          // the upload row being viewed
  const [detail, setDetail] = useState({ status: 'idle', ids: [], activities: null });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/agent/uploads`, { params: { limit: 50 } });
      setState({ status: 'ready', items: Array.isArray(data) ? data : [] });
    } catch {
      setState((s) => ({ ...s, status: 'error' }));
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openUpload = async (up) => {
    setOpen(up);
    setDetail({ status: 'loading', ids: [], activities: null });
    try {
      const { data } = await axios.get(`${API}/agent/uploads/${up.id}`);
      let activities = null;
      if (up.activity_type !== 'freight') {
        const res = await axios.get(`${API}/scope3/activities`, { params: { limit: 1000 } });
        activities = Array.isArray(res.data) ? res.data : [];
      }
      setDetail({ status: 'ready', ids: data.entry_ids || [], activities });
    } catch {
      setDetail({ status: 'error', ids: [], activities: null });
    }
  };

  const entries = useMemo(() => {
    if (!open || detail.status !== 'ready') return [];
    const want = new Set(detail.ids);
    const pool = open.activity_type === 'freight' ? shipments : detail.activities || [];
    return pool.filter((e) => want.has(e.id));
  }, [open, detail, shipments]);
  const missing = open && detail.status === 'ready' ? Math.max(0, detail.ids.length - entries.length) : 0;
  const shownKg = entries.reduce((s, e) => s + Number(open?.activity_type === 'freight' ? e.total_emissions : e.co2e_kg) || 0, 0);

  return (
    <section aria-labelledby="upload-history-title" className="rounded-xl border bg-card p-5 shadow sm:p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 id="upload-history-title" className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
            <History className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Past uploads
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">Open a file to see only the entries saved from it</p>
        </div>
        {state.status === 'ready' && state.items.length > 0 && (
          <span className="text-xs text-muted-foreground">{state.items.length} file{state.items.length === 1 ? '' : 's'}</span>
        )}
      </div>

      {state.status === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}
      {state.status === 'error' && (
        <p className="text-sm text-muted-foreground">
          Couldn’t load your past uploads.{' '}
          <button type="button" onClick={load} className="font-semibold text-primary hover:underline">Try again</button>
        </p>
      )}
      {state.status === 'ready' && state.items.length === 0 && (
        <p className="text-sm text-muted-foreground">No uploads yet — files you save from the wizard above will appear here.</p>
      )}

      {state.status === 'ready' && state.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="pb-2 pr-3 font-semibold">File</th>
                <th className="pb-2 pr-3 font-semibold">Category</th>
                <th className="pb-2 pr-3 text-right font-semibold">Rows saved</th>
                <th className="pb-2 pr-3 text-right font-semibold">t CO₂e</th>
                <th className="hidden pb-2 pr-3 font-semibold md:table-cell">Uploaded</th>
                <th className="w-8 pb-2" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {state.items.map((u) => (
                <tr
                  key={u.id}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${u.filename}`}
                  onClick={() => openUpload(u)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openUpload(u); } }}
                  className="cursor-pointer border-t border-border/70 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
                >
                  <td className="py-3 pr-3">
                    <span className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-secondary text-primary">
                        <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground" title={u.filename}>{u.filename}</span>
                        <span className="block text-xs text-muted-foreground">{TYPE_LABEL[u.activity_type] || u.activity_type}</span>
                      </span>
                    </span>
                  </td>
                  <td className="py-3 pr-3"><CatPill value={u.ghg_category} palette={palette} /></td>
                  <td className="whitespace-nowrap py-3 pr-3 text-right tabular-nums">
                    {u.saved_rows.toLocaleString()}<span className="text-muted-foreground"> / {u.total_rows.toLocaleString()}</span>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-right font-semibold tabular-nums text-foreground">{fmtNumber(u.total_co2e_kg / 1000, 2)}</td>
                  <td className="hidden whitespace-nowrap py-3 pr-3 text-muted-foreground md:table-cell">{fmtDate(u.created_at, true)}</td>
                  <td className="py-3 text-right"><ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" aria-hidden="true" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* One file's entries */}
      <Sheet open={!!open} onOpenChange={(v) => { if (!v) setOpen(null); }}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
          {open && (
            <>
              <SheetHeader className="space-y-1 pr-6 text-left">
                <SheetTitle className="flex items-center gap-2 break-all text-lg">
                  <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />
                  {open.filename}
                </SheetTitle>
                <SheetDescription>
                  Uploaded {fmtDate(open.created_at, true)} · {TYPE_LABEL[open.activity_type] || open.activity_type}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  ['Rows saved', `${open.saved_rows.toLocaleString()} / ${open.total_rows.toLocaleString()}`],
                  ['Total', `${fmtNumber(open.total_co2e_kg / 1000, 2)} t CO₂e`],
                  ['Category', null],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
                    <p className="text-xs text-muted-foreground">{k}</p>
                    <div className="mt-1 text-sm font-semibold tabular-nums text-foreground">
                      {v ?? <CatPill value={open.ghg_category} palette={palette} />}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                {detail.status === 'loading' && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading entries…</p>
                )}
                {detail.status === 'error' && <p className="text-sm text-muted-foreground">Couldn’t load this file’s entries.</p>}
                {detail.status === 'ready' && (
                  <>
                    <div className="mb-2 flex items-baseline justify-between gap-2">
                      <h4 className="text-sm font-semibold text-foreground">Entries from this file ({entries.length})</h4>
                      {entries.length > 0 && <span className="text-xs tabular-nums text-muted-foreground">{fmtNumber(shownKg, 1)} kg CO₂e</span>}
                    </div>
                    {missing > 0 && (
                      <p className="mb-3 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                        {missing} entr{missing === 1 ? 'y has' : 'ies have'} been deleted since this upload.
                      </p>
                    )}
                    {entries.length === 0 ? (
                      <p className="text-sm text-muted-foreground">None of this file’s entries are still saved.</p>
                    ) : open.activity_type === 'freight' ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              <th className="pb-2 pr-3 font-semibold">Goods</th>
                              <th className="pb-2 pr-3 font-semibold">Route</th>
                              <th className="pb-2 pr-3 text-right font-semibold">Distance</th>
                              <th className="pb-2 text-right font-semibold">kg CO₂e</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.map((s) => (
                              <tr key={s.id} className="border-t border-border/70 align-top">
                                <td className="py-2.5 pr-3">
                                  <p className="font-medium text-foreground">{s.good?.name}</p>
                                  <p className="text-xs text-muted-foreground">{fmtNumber(s.good?.quantity, 2)} {s.good?.unit === 'tons' ? 't' : s.good?.unit} · {CATEGORY[s.good?.ghg_category]?.label || s.good?.ghg_category}</p>
                                </td>
                                <td className="py-2.5 pr-3">
                                  <ul className="space-y-0.5">
                                    {(s.transport_legs || []).map((leg, i) => (
                                      <li key={i} className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <span className="font-medium text-foreground">{modeName(leg.transport_mode)}</span>
                                        {isManualLeg(leg) ? <ManualDistanceIcon /> : <span>· {shortPlace(leg.from_location?.address)} → {shortPlace(leg.to_location?.address)}</span>}
                                        <span className="hidden lg:inline">· {vehicleName(leg.vehicle_type)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                                <td className="whitespace-nowrap py-2.5 pr-3 text-right tabular-nums">{fmtNumber(s.total_distance, 1)} km</td>
                                <td className="whitespace-nowrap py-2.5 text-right font-semibold tabular-nums text-foreground">{fmtNumber(s.total_emissions, 2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              <th className="pb-2 pr-3 font-semibold">Date</th>
                              <th className="pb-2 pr-3 font-semibold">Mode</th>
                              <th className="pb-2 pr-3 font-semibold">Details</th>
                              <th className="pb-2 pr-3 text-right font-semibold">Activity</th>
                              <th className="pb-2 text-right font-semibold">kg CO₂e</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.map((a) => (
                              <tr key={a.id} className="border-t border-border/70 align-top">
                                <td className="whitespace-nowrap py-2.5 pr-3 text-muted-foreground">{a.activity_date ? fmtDate(a.activity_date) : '—'}</td>
                                <td className="whitespace-nowrap py-2.5 pr-3 text-foreground">{modeName(a.mode)}</td>
                                <td className="py-2.5 pr-3 text-foreground">
                                  {a.subtype || modeName(a.mode)}
                                  {a.origin && a.destination && <span className="block text-xs text-muted-foreground">{a.origin} → {a.destination}</span>}
                                </td>
                                <td className="whitespace-nowrap py-2.5 pr-3 text-right tabular-nums text-muted-foreground">{fmtNumber(a.activity_value)} {a.activity_unit}</td>
                                <td className="whitespace-nowrap py-2.5 text-right font-semibold tabular-nums text-foreground">{fmtNumber(a.co2e_kg, 2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
